import {
    collection,
    deleteDoc,
    deleteField,
    doc,
    getDocs,
    query,
    serverTimestamp,
    setDoc,
    where,
    writeBatch,
} from 'firebase/firestore';
import { db } from '../firebase';
import { Portfolio, Transaction } from '../types';
import { upsertQuoteWatchlistSymbols } from './quoteWatchlistService';
import { aggregateHoldings } from '../utils/holdingAggregation';
import { buildSyntheticHoldingBuyTransaction } from './portfolioSnapshotMapping';

const WRITE_CHUNK_SIZE = 400;
const IMPORT_HOLDING_COLLECTIONS = ['import_holding', 'import_holdings'] as const;
const IMPORT_HOLDING_WRITE_COLLECTION = IMPORT_HOLDING_COLLECTIONS[0];

const toNumber = (value: unknown): number => {
    const n = Number(value);
    return Number.isFinite(n) ? n : 0;
};

const simpleHash = (input: string) => {
    let hash = 2166136261;
    for (let i = 0; i < input.length; i += 1) {
        hash ^= input.charCodeAt(i);
        hash = Math.imul(hash, 16777619);
    }
    return (hash >>> 0).toString(16).padStart(8, '0');
};

const toIsoDateOnly = (value: string | undefined) => {
    if (!value) return undefined;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return undefined;
    return date.toISOString().slice(0, 10);
};

const timestampToIsoStringOrUndefined = (value: unknown): string | undefined => {
    if (!value) return undefined;
    if (typeof value === 'string') {
        const date = new Date(value);
        return Number.isNaN(date.getTime()) ? undefined : date.toISOString();
    }
    if (typeof value === 'number') {
        const date = new Date(value);
        return Number.isNaN(date.getTime()) ? undefined : date.toISOString();
    }
    if (typeof value === 'object' && value !== null && 'toDate' in value && typeof (value as { toDate?: unknown }).toDate === 'function') {
        const date = (value as { toDate: () => Date }).toDate();
        if (!Number.isNaN(date.getTime())) return date.toISOString();
    }
    return undefined;
};

const timestampToIsoString = (value: unknown) => timestampToIsoStringOrUndefined(value) || new Date().toISOString();

const timestampToMillis = (value: unknown) => {
    const iso = timestampToIsoStringOrUndefined(value);
    if (!iso) return Number.POSITIVE_INFINITY;
    return new Date(iso).getTime();
};

type ImportHoldingMetadata = {
    createdAt?: unknown;
    lastTradeDate?: unknown;
};

const getEarliestImportHoldingMetadata = (docs: Array<{ data: () => Record<string, unknown> }>) => {
    const byHoldingId = new Map<string, ImportHoldingMetadata>();

    docs.forEach((importHoldingDoc) => {
        const importHolding = importHoldingDoc.data();
        const holdingId = String(importHolding.holdingId || '').trim();
        if (!holdingId) return;

        const existing = byHoldingId.get(holdingId);
        if (!existing || timestampToMillis(importHolding.createdAt) < timestampToMillis(existing.createdAt)) {
            byHoldingId.set(holdingId, {
                createdAt: importHolding.createdAt,
                lastTradeDate: importHolding.lastTradeDate,
            });
        }
    });

    return byHoldingId;
};

const commitInChunks = async <T>(items: T[], fn: (batch: ReturnType<typeof writeBatch>, item: T) => void) => {
    for (let i = 0; i < items.length; i += WRITE_CHUNK_SIZE) {
        const chunk = items.slice(i, i + WRITE_CHUNK_SIZE);
        const batch = writeBatch(db);
        chunk.forEach((item) => fn(batch, item));
        await batch.commit();
    }
};

type WatchlistSeed = {
    symbol: string;
    name?: string;
    exchange?: string;
    currency?: string;
    assetType?: 'equity' | 'etf' | 'crypto' | 'fx' | 'index';
    instrumentId?: string;
    providerKey?: string;
};

const collectPortfolioWatchlistItems = (portfolios: Portfolio[]): WatchlistSeed[] => {
    const deduped = new Map<string, WatchlistSeed>();

    portfolios.forEach((portfolio) => {
        const holdings = aggregateHoldings((portfolio.transactions || []).filter((tx) => !tx.deletedAt));

        holdings.forEach((holding) => {
            const symbol = String(holding.symbol || '').trim().toUpperCase();
            if (!symbol || symbol === 'CASH') return;

            const existing = deduped.get(symbol) || { symbol };

            deduped.set(symbol, {
                ...existing,
                symbol,
                name: holding.name || existing.name,
                exchange: holding.exchange || existing.exchange,
                currency: holding.currency || existing.currency,
                assetType: existing.assetType || 'equity',
                instrumentId: holding.instrumentId || existing.instrumentId,
                providerKey: holding.providerKey || existing.providerKey,
            });
        });
    });

    return Array.from(deduped.values());
};


const syncPortfolio = async (uid: string, portfolio: Portfolio, importSource: string = 'other') => {
    const headerRef = doc(db, 'users', uid, 'portfolios', portfolio.id);
    const importId = `imp_${Date.now()}_${simpleHash(`${portfolio.id}:${portfolio.transactions.length}`)}`;

    const inferredBaseCurrency = String(
        portfolio.transactions.find((tx) => String(tx.currency || '').trim())?.currency ||
            portfolio.transactions.find((tx) => String(tx.priceCurrency || '').trim())?.priceCurrency ||
            'USD'
    )
        .trim()
        .toUpperCase();

    await setDoc(
        headerRef,
        {
            name: portfolio.name,
            baseCurrency: inferredBaseCurrency,
            createdAt: portfolio.created || Date.now(),
            updatedAt: serverTimestamp(),
            status: 'importing',
            importSource,
            keepTradeHistory: false,
            errorMessage: deleteField(),
            transactions: deleteField(),
            holdings: deleteField(),
            rawRows: deleteField(),
            csvText: deleteField(),
        },
        { merge: true }
    );

    const liveTransactions = (portfolio.transactions || []).filter((tx) => !tx.deletedAt);
    const holdings = aggregateHoldings(liveTransactions);
    const holdingIds = new Set(holdings.map((h) => h.holdingId));
    const holdingsCollectionPath = ['users', uid, 'portfolios', portfolio.id, 'holdings'];
    const existingHoldingsSnap = await getDocs(collection(db, ...holdingsCollectionPath));
    const existingHoldingDataById = new Map(existingHoldingsSnap.docs.map((d) => [d.id, d.data() as Record<string, unknown>]));
    const missingHoldingIds = existingHoldingsSnap.docs.map((d) => d.id).filter((id) => !holdingIds.has(id));
    await commitInChunks(missingHoldingIds, (batch, id) => {
        batch.set(
            doc(db, ...holdingsCollectionPath, id),
            {
                active: false,
                inactiveAt: serverTimestamp(),
                updatedAt: serverTimestamp(),
                lastSeenImportId: importId,
            },
            { merge: true }
        );
    });

    await commitInChunks(holdings, (batch, holding) => {
        const existingHolding = existingHoldingDataById.get(holding.holdingId);
        const hasCreatedAt = existingHolding && Object.prototype.hasOwnProperty.call(existingHolding, 'createdAt');

        batch.set(doc(db, ...holdingsCollectionPath, holding.holdingId), {
            instrumentId: holding.instrumentId || null,
            providerKey: holding.providerKey || null,
            symbol: holding.symbol,
            displaySymbol: holding.displaySymbol,
            name: holding.name,
            exchange: holding.exchange || null,
            currency: holding.currency,
            costCurrency: holding.costCurrency,
            quantity: holding.quantity,
            avgCost: holding.avgCost,
            avgCostNative: holding.avgCostNative,
            costBasis: holding.costBasis,
            costBasisNative: holding.costBasisNative,
            dividendsTotal: holding.dividendsTotal,
            lastTradeDate: holding.lastTradeDate || null,
            source: 'csv',
            active: true,
            createdAt: hasCreatedAt ? existingHolding?.createdAt : serverTimestamp(),
            inactiveAt: deleteField(),
            lastSeenImportId: importId,
            updatedAt: serverTimestamp(),
        }, { merge: true });
    });

    const importHoldingsCollectionPath = ['users', uid, 'portfolios', portfolio.id, IMPORT_HOLDING_WRITE_COLLECTION];
    await commitInChunks(holdings, (batch, holding) => {
        batch.set(doc(db, ...importHoldingsCollectionPath, `${importId}_${holding.holdingId}`), {
            importId,
            holdingId: holding.holdingId,
            instrumentId: holding.instrumentId || null,
            providerKey: holding.providerKey || null,
            symbol: holding.symbol,
            displaySymbol: holding.displaySymbol,
            name: holding.name,
            exchange: holding.exchange || null,
            currency: holding.currency,
            costCurrency: holding.costCurrency,
            totalBuysQty: holding.totalBuysQty,
            totalBuysAmount: holding.totalBuysAmount,
            totalSellsQty: holding.totalSellsQty,
            totalSellsAmount: holding.totalSellsAmount,
            netQuantity: holding.quantity,
            costBasis: holding.costBasis,
            costBasisNative: holding.costBasisNative,
            avgCost: holding.avgCost,
            avgCostNative: holding.avgCostNative,
            dividendsTotal: holding.dividendsTotal,
            lastTradeDate: holding.lastTradeDate || null,
            source: importSource === 'manual' ? 'manual' : 'csv',
            createdAt: serverTimestamp(),
        });
    });

    const tradeDates = liveTransactions
        .map((tx) => toIsoDateOnly(tx.date))
        .filter((value): value is string => Boolean(value));
    const dateRange = tradeDates.length
        ? {
              start: tradeDates.reduce((min, date) => (date < min ? date : min), tradeDates[0]),
              end: tradeDates.reduce((max, date) => (date > max ? date : max), tradeDates[0]),
          }
        : null;

    await setDoc(doc(db, 'users', uid, 'portfolios', portfolio.id, 'imports', importId), {
        fileHash: simpleHash(JSON.stringify(liveTransactions.map((tx) => [tx.id, tx.date, tx.action, tx.symbol, tx.amount]))),
        dateRange,
        rowCount: liveTransactions.length,
        uniqueSymbols: holdings.length,
        keepTradeHistory: false,
        status: 'ready',
        createdAt: serverTimestamp(),
    });

    await setDoc(
        headerRef,
        {
            status: 'ready',
            lastImportAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
            rowCount: liveTransactions.length,
            uniqueSymbols: holdings.length,
            lastImportDateEnd: dateRange?.end || null,
        },
        { merge: true }
    );
};

export const syncPortfoliosToFirestore = async (uid: string, portfolios: Portfolio[]) => {
    const existingPortfolios = await getDocs(collection(db, 'users', uid, 'portfolios'));
    const nextIds = new Set(portfolios.map((p) => p.id));

    for (const existing of existingPortfolios.docs) {
        if (!nextIds.has(existing.id)) {
            await deleteDoc(existing.ref);
        }
    }

    for (const portfolio of portfolios) {
        try {
            const inferredSource = portfolio.transactions.some((tx) => tx.source === 'csv') ? 'trading212' : 'manual';
            await syncPortfolio(uid, portfolio, inferredSource);
        } catch (error) {
            await setDoc(
                doc(db, 'users', uid, 'portfolios', portfolio.id),
                {
                    status: 'failed',
                    errorMessage: error instanceof Error ? error.message.slice(0, 300) : 'Unknown import failure',
                    updatedAt: serverTimestamp(),
                },
                { merge: true }
            );
            throw error;
        }
    }

    const itemsToTrack = collectPortfolioWatchlistItems(portfolios);
    if (itemsToTrack.length) {
        await upsertQuoteWatchlistSymbols(uid, itemsToTrack);
    }
};

export const loadPortfoliosWithTransactions = async (uid: string): Promise<Portfolio[]> => {
    const headersSnap = await getDocs(collection(db, 'users', uid, 'portfolios'));
    const portfolios = await Promise.all(
        headersSnap.docs.map(async (headerDoc) => {
            const data = headerDoc.data() as Record<string, unknown>;
            const holdingsSnap = await getDocs(
                query(collection(db, 'users', uid, 'portfolios', headerDoc.id, 'holdings'), where('active', '==', true))
            );
            const importHoldingSnapshots = await Promise.all(
                IMPORT_HOLDING_COLLECTIONS.map((subcollection) =>
                    getDocs(collection(db, 'users', uid, 'portfolios', headerDoc.id, subcollection))
                )
            );
            const importHoldingMetadataById = getEarliestImportHoldingMetadata(
                importHoldingSnapshots.flatMap((snapshot) => snapshot.docs)
            );
            const lastImportAt = timestampToIsoString(data.lastImportAt);
            const snapshotAsOf = typeof data.lastImportDateEnd === 'string' ? data.lastImportDateEnd : undefined;
            const holdingTransactions = holdingsSnap.docs.map((holdingDoc) => {
                const holding = holdingDoc.data() as Record<string, unknown>;
                const importMetadata = importHoldingMetadataById.get(holdingDoc.id);
                const holdingWithCreationDate = {
                    ...holding,
                    createdAt: importMetadata?.createdAt || holding.createdAt,
                    lastTradeDate: importMetadata?.lastTradeDate || holding.lastTradeDate,
                };
                return buildSyntheticHoldingBuyTransaction(holdingDoc.id, holdingWithCreationDate, lastImportAt, snapshotAsOf, data);
            });

            const syntheticDividendTransactions = holdingsSnap.docs
                .map((holdingDoc) => {
                    const holding = holdingDoc.data() as Record<string, unknown>;
                    const importMetadata = importHoldingMetadataById.get(holdingDoc.id);
                    const createdAt = importMetadata?.createdAt || holding.createdAt || data.createdAt || data.created;
                    const dividendsTotal = toNumber(holding.dividendsTotal);
                    if (!(dividendsTotal > 0)) return null;
                    return {
                        id: `holding_div_${holdingDoc.id}`,
                        action: 'Dividend',
                        symbol: String(holding.displaySymbol || holding.symbol || ''),
                        displaySymbol: String(holding.displaySymbol || holding.symbol || ''),
                        name: String(holding.name || holding.displaySymbol || holding.symbol || 'Unknown'),
                        date: timestampToIsoStringOrUndefined(createdAt) || lastImportAt,
                        createdAt: timestampToIsoStringOrUndefined(createdAt),
                        quantity: 0,
                        price: 0,
                        amount: dividendsTotal,
                        currency: String(holding.costCurrency || holding.currency || data.baseCurrency || 'USD'),
                        source: 'snapshot',
                        sourceLabel: 'Imported dividend total',
                        isSynthetic: true,
                        snapshotAsOf,
                        instrumentId: typeof holding.instrumentId === 'string' ? holding.instrumentId : undefined,
                        exchange: typeof holding.exchange === 'string' ? holding.exchange : undefined,
                    } as Transaction;
                })
                .filter((tx): tx is Transaction => Boolean(tx));

            const transactions = [...holdingTransactions, ...syntheticDividendTransactions];

            return {
                id: headerDoc.id,
                name: String(data.name || 'Portfolio'),
                created: toNumber(data.createdAt || data.created) || Date.now(),
                transactions,
            } as Portfolio;
        })
    );

    return portfolios;
};
